import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";
import { Instagram } from "lucide-react";
import { useLanguage } from "@/i18n";
import { SectionHeading } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHOTS = [
  {
    img: "https://images.unsplash.com/photo-1485465109746-b76ae8d7eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxvZmZyb2FkJTIwNHg0JTIwZGVzZXJ0JTIwbXVkfGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
    label: "ERG CHEBBI — 31.15°N 3.98°W",
  },
  {
    img: "https://images.unsplash.com/photo-1605019659411-f6dd1d229f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxvdmVybGFuZCUyMGNhbXBpbmclMjA0eDQlMjBleHBlZGl0aW9ufGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
    label: "ALPI GIULIE — 46.38°N 13.58°E",
  },
  {
    img: "https://images.unsplash.com/photo-1536383794906-773f6a5210be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxvZmZyb2FkJTIwNHg0JTIwZGVzZXJ0JTIwbXVkfGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
    label: "APPENNINO — 44.19°N 10.94°E",
  },
  {
    img: "https://images.unsplash.com/photo-1771186936875-f80e00ec1127?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxkZXNlcnQlMjBkdW5lJTIwc3Vuc2V0JTIwb3ZlcmxhbmR8ZW58MHx8fHwxNzg4ODk4Mzg4fDA&ixlib=rb-4.1.0&q=85",
    label: "BARDENAS — 42.18°N 1.52°W",
  },
  {
    img: "https://images.unsplash.com/photo-1636138105085-3e9381a8e438?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxvdmVybGFuZCUyMGNhbXBpbmclMjA0eDQlMjBleHBlZGl0aW9ufGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
    label: "ALTO ATLANTE — 31.06°N 7.92°W",
  },
  {
    img: "https://images.unsplash.com/photo-1672334440489-7945a743b823?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxvZmZyb2FkJTIwNHg0JTIwZGVzZXJ0JTIwbXVkfGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
    label: "VAL D'ORCIA — 43.06°N 11.61°E",
  },
];

export default function MediaMarquee() {
  const { t } = useLanguage();
  const loop = [...SHOTS, ...SHOTS];
  const [pins, setPins] = useState([]);
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    axios.get(`${API}/instagram/pinned`).then((r) => setPins(r.data)).catch(() => {});
    axios.get(`${API}/instagram/feed`).then((r) => setFeed(r.data)).catch(() => setFeed({ configured: false, items: [] }));
  }, []);

  useEffect(() => {
    if (!pins.length) return;
    const process = () => window.instgrm?.Embeds?.process();
    if (window.instgrm) {
      process();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.instagram.com/embed.js";
    s.async = true;
    s.onload = process;
    document.body.appendChild(s);
  }, [pins]);
  return (
    <section id="media" className="py-28 sm:py-36 bg-surface/40 border-y border-line overflow-hidden" data-testid="media-section">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow={t.media.eyebrow} title={t.media.title} sub={t.media.sub} />
      </div>
      {feed && feed.configured && feed.items.length > 0 ? (
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 mb-16" data-testid="ig-live-grid">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <p className="font-telemetry text-xs uppercase tracking-[0.25em] text-blaze flex items-center gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blaze animate-pulse-dot" />
              {t.media.latest}
            </p>
            <a
              href="https://instagram.com/dust_republic"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ig-follow-link"
              className="flex items-center gap-2 font-telemetry text-[10px] uppercase tracking-[0.2em] border border-line px-4 py-2 text-sand hover:border-blaze hover:text-blaze transition-colors"
            >
              <Instagram size={13} />
              {t.media.follow}
            </a>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {feed.items.map((item) => (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ig-live-${item.id}`}
                className="group relative border border-line overflow-hidden aspect-square block"
              >
                <img
                  src={item.image}
                  alt={item.caption || "Dust Republic Instagram"}
                  loading="lazy"
                  className="w-full h-full object-cover warm-grade group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <Instagram size={15} className="absolute top-3 right-3 text-bone/80" />
                {item.caption && (
                  <p className="absolute bottom-3 left-3 right-3 text-[11px] leading-snug text-sand opacity-0 group-hover:opacity-100 transition-opacity duration-500 line-clamp-2">
                    {item.caption}
                  </p>
                )}
              </a>
            ))}
          </div>
        </div>
      ) : pins.length > 0 ? (
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 mb-16" data-testid="ig-pinned-grid">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <p className="font-telemetry text-xs uppercase tracking-[0.25em] text-blaze">
              {t.media.latest}
            </p>
            <a
              href="https://instagram.com/dust_republic"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ig-follow-link"
              className="flex items-center gap-2 font-telemetry text-[10px] uppercase tracking-[0.2em] border border-line px-4 py-2 text-sand hover:border-blaze hover:text-blaze transition-colors"
            >
              <Instagram size={13} />
              {t.media.follow}
            </a>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pins.slice(0, 6).map((p) => (
              <div key={p.id} data-testid={`ig-embed-${p.id}`} className="border border-line bg-bone p-1 min-h-[320px] overflow-hidden">
                <blockquote
                  className="instagram-media"
                  data-instgrm-permalink={`${p.url}?utm_source=ig_embed`}
                  data-instgrm-version="14"
                  style={{ margin: 0, width: "100%", minWidth: 0 }}
                >
                  <a href={p.url} target="_blank" rel="noopener noreferrer">
                    {p.url}
                  </a>
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="overflow-hidden"
      >
        <div className="flex w-max animate-marquee-slow gap-5 px-5">
          {loop.map((s, i) => (
            <figure
              key={i}
              className="relative w-[300px] sm:w-[430px] h-[210px] sm:h-[280px] shrink-0 overflow-hidden border border-line group"
            >
              <img
                src={s.img}
                alt={s.label}
                loading="lazy"
                className="w-full h-full object-cover warm-grade group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
              <span className="absolute top-3 right-3 flex items-center gap-1.5 font-telemetry text-[9px] tracking-[0.2em] text-[#E86A50]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D94235] animate-pulse-dot" />
                {t.media.badge}
              </span>
              <figcaption className="absolute bottom-3 left-3 font-telemetry text-[10px] tracking-[0.2em] uppercase text-sand/90">
                {s.label}
              </figcaption>
            </figure>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
